"use client";

import React, { useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

const onlineItems = [
  { icon: 'ShoppingBagIcon', text: '入駐淘寶、天貓旗艦店' },
  { icon: 'ShoppingCartIcon', text: '京東自營、POP店鋪運營' },
  { icon: 'DevicePhoneMobileIcon', text: '拼多多、抖音電商等新渠道' },
  { icon: 'GlobeAltIcon', text: '亞馬遜、速賣通跨境電商' },
  { icon: 'TruckIcon', text: '為電商賣家提供代發貨服務' },
  { icon: 'BuildingStorefrontIcon', text: '私域電商、直播帶貨供應' },
];

const offlineItems = [
  { icon: 'BuildingOfficeIcon', text: '全國批發商、代理商合作' },
  { icon: 'ShoppingBagIcon', text: '商超、便利店、連鎖零售採購' },
  { icon: 'HomeIcon', text: '母嬰店、家居用品店專供' },
  { icon: 'WrenchIcon', text: 'OEM/ODM品牌定制服務' },
  { icon: 'DocumentTextIcon', text: '企業採購、禮品定制' },
  { icon: 'BuildingStorefrontIcon', text: '展會合作、現貨批發' },
];

export default function BusinessChannels() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => { if (e.isIntersecting) e.target.classList.remove('reveal-hidden'); });
      },
      { threshold: 0.1 }
    );
    sectionRef.current?.querySelectorAll('.reveal').forEach((el) => {
      el.classList.add('reveal-hidden');
      observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 px-6 bg-white">
      <div className="max-w-container mx-auto">
        <div className="text-center mb-14 reveal">
          <p className="text-xs font-semibold tracking-widest uppercase text-accent mb-2">
            DUAL CHANNELS · 雙渠道模式
          </p>
          <h2 className="font-heading font-bold text-brand-fg text-3xl md:text-4xl">
            線上電商 + 線下批發
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Online */}
          <div
            className="reveal rounded-3xl p-8 border-2 border-primary/20"
            style={{ background: 'linear-gradient(135deg, #FDFAF6, #F5EFE6)' }}
          >
            <div className="flex items-center gap-4 mb-6">
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl"
                style={{ background: 'linear-gradient(135deg, #8B5E3C, #C4915A)' }}
              >
                🛒
              </div>
              <div>
                <h3 className="font-heading font-bold text-brand-fg text-2xl">線上電商板塊</h3>
                <p className="text-brand-muted text-sm">Online E-Commerce</p>
              </div>
            </div>
            <div className="space-y-3">
              {onlineItems.map((item) => (
                <div key={item.text} className="flex items-center gap-3">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: 'rgba(139,94,60,0.10)' }}
                  >
                    <Icon name={item.icon as any} size={16} className="text-primary" />
                  </div>
                  <p className="text-sm text-brand-fg">{item.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Offline */}
          <div
            className="reveal rounded-3xl p-8 border-2 border-accent/20"
            style={{ background: 'linear-gradient(135deg, #FEF9F3, #FDF3E8)', transitionDelay: '120ms' }}
          >
            <div className="flex items-center gap-4 mb-6">
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl"
                style={{ background: 'linear-gradient(135deg, #C4915A, #8B5E3C)' }}
              >
                🏭
              </div>
              <div>
                <h3 className="font-heading font-bold text-brand-fg text-2xl">線下批發板塊</h3>
                <p className="text-brand-muted text-sm">Offline Wholesale</p>
              </div>
            </div>
            <div className="space-y-3">
              {offlineItems.map((item) => (
                <div key={item.text} className="flex items-center gap-3">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: 'rgba(196,145,90,0.12)' }}
                  >
                    <Icon name={item.icon as any} size={16} className="text-accent" />
                  </div>
                  <p className="text-sm text-brand-fg">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}